import {
  Typography,
  Stack,
  Paper,
  Drawer,
  Box,
  IconButton,
  ListItem,
  Skeleton,
  useTheme
} from "@mui/material";
import {
  ArrowRight24Px,
  Attention20Px,
  Clock20Px,
  Warning20Px,
  formatTimeInTimeZone
} from "@qubit/autoparts";
import { SerializedError } from "@reduxjs/toolkit";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import dayjs from "dayjs";
import { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { FulfillmentCenterDto, OrderSummaryDto } from "~/types/api";

import { NoOrders } from "./NoOrders";
import { OrderSidePanel } from "./OrderSidePanel";
import { OrderStatus, OrdersSearch } from "./OrdersSearch";

type OrdersTableProps = {
  formatOrderStatus?: (order: OrderSummaryDto) => string;
  fulfillmentCenter: FulfillmentCenterDto | null;
  orders: OrderSummaryDto[];
  ordersLoading: boolean;
  ordersError?: FetchBaseQueryError | SerializedError;
  selectedDate: Date | null;
  selectedOrderIds: Guid[];
  title: string;
  fetchNextPage: () => void;
};

export function OrdersTable(props: OrdersTableProps) {
  const {
    formatOrderStatus = undefined,
    fulfillmentCenter,
    orders,
    ordersLoading,
    selectedOrderIds,
    ordersError,
    fetchNextPage
  } = props;

  const { t } = useTranslation();
  const palette = useTheme().palette;

  // infinite scroll
  const observer = useRef<IntersectionObserver | null>(null);
  const lastOrderRef = useCallback(
    (node: HTMLLIElement | null) => {
      if (ordersLoading) return;
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting) {
          fetchNextPage();
        }
      });

      if (node) observer.current.observe(node);
    },
    [ordersLoading, fetchNextPage]
  );

  const defaultOrderStatusFormat = (order: OrderSummaryDto) => order.status;
  const formatOrderStatus2 = formatOrderStatus || defaultOrderStatusFormat;

  const [orderDetailsId, setOrderDetailsId] = useState<Guid | null>(null);

  const mainOrderInfo = (order: OrderSummaryDto) => {
    return (
      <Stack direction="column" alignItems="flex-start" width={"25%"}>
        <Typography variant="subtitle1">{order.customerName}</Typography>
        <Typography variant="caption" color="textSecondary">
          {order.externalOrderId}
        </Typography>
      </Stack>
    );
  };

  const secondaryOrderInfo = (text: string, label: string) => {
    return (
      <Stack direction="column" minWidth={"60px"} alignItems="flex-start">
        <Typography variant="subtitle1">{text}</Typography>
        <Typography variant="caption" color="textSecondary">
          {label}
        </Typography>
      </Stack>
    );
  };

  const orderSatus = (order: OrderSummaryDto) =>
    !formatOrderStatus
      ? t(`${formatOrderStatus2(order).toLowerCase()}` as OrderStatus)
      : formatOrderStatus2(order);

  const noOrders =
    !ordersLoading && orders && orders.length === 0 && !ordersError;

  return (
    <>
      <Paper
        id="order-list-container"
        role="list"
        sx={{ padding: 1, backgroundColor: "gray.light" }}
      >
        <Box mb={1} maxWidth="375px">
          <OrdersSearch />
        </Box>
        <Stack
          direction="column"
          gap={1}
          sx={{
            maxHeight: "calc(100dvh - 300px)",
            overflowY: "auto"
          }}
        >
          {noOrders && <NoOrders />}
          {ordersLoading && (
            <Skeleton
              variant="rounded"
              height={80}
              aria-label="loading order"
            />
          )}
          {!ordersLoading &&
            orders.map((order, index) => {
              const isSelected = selectedOrderIds.includes(order.orderId);
              const isLastItem = index === orders.length - 1;

              const pickingEndTime = dayjs(order.pickingEndTime).tz(
                fulfillmentCenter?.timeZone
              );
              const isPickingEndTimeDueSoon =
                pickingEndTime.diff(
                  dayjs().tz(fulfillmentCenter?.timeZone),
                  "hour",
                  true
                ) <= 2;
              const isPastDue = pickingEndTime.isBefore(
                dayjs().tz(fulfillmentCenter?.timeZone)
              );
              let startIcon = <Box width={24} />;
              if (isPastDue) {
                startIcon = (
                  <Attention20Px
                    aria-label="past due"
                    style={{ fill: palette.error.main }}
                  />
                );
              } else if (isPickingEndTimeDueSoon) {
                startIcon = (
                  <Warning20Px
                    aria-label="due soon"
                    style={{ fill: palette.warning.main }}
                  />
                );
              }

              return (
                <ListItem
                  key={order.orderId}
                  ref={isLastItem ? lastOrderRef : null}
                  onClick={() => setOrderDetailsId(order.orderId)}
                  role="listitem"
                  sx={{
                    padding: 0,
                    cursor: "pointer"
                  }}
                >
                  <Paper
                    sx={{
                      width: "100%",
                      overflow: "hidden",
                      userSelect: "text",
                      backgroundColor: isSelected ? "primary.light" : "white",
                      border: "3px solid",
                      borderColor: isSelected ? "darkBlue.main" : "transparent"
                    }}
                  >
                    <Stack
                      padding={2}
                      width="auto"
                      direction="row"
                      gap={2}
                      justifyContent="space-between"
                    >
                      {mainOrderInfo(order)}
                      {secondaryOrderInfo(orderSatus(order), t("order status"))}
                      {secondaryOrderInfo(
                        order.orderType ?? "-",
                        t("order type")
                      )}
                      {secondaryOrderInfo(
                        `${order.toteCount} ${t("totes")}`,
                        t("# of totes")
                      )}
                      {secondaryOrderInfo(
                        order.earliestPickingStartTime
                          ? formatTimeInTimeZone(
                              order.earliestPickingStartTime,
                              fulfillmentCenter?.timeZone
                            )
                          : " - ",
                        t("earliest start time")
                      )}
                      <Stack direction="row" alignItems="center" gap={1}>
                        {startIcon}
                        <Stack
                          direction="column"
                          minWidth={"60px"}
                          alignItems="flex-start"
                        >
                          <Typography
                            variant="subtitle1"
                            color={isPastDue ? "error" : "inherit"}
                          >
                            {formatTimeInTimeZone(
                              order.pickingEndTime,
                              fulfillmentCenter?.timeZone
                            )}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {t("picking end time")}
                          </Typography>
                        </Stack>
                      </Stack>
                      <Stack direction="row" alignItems="center" gap={1}>
                        {order.priority.toLowerCase() === "express" ? (
                          <Clock20Px />
                        ) : (
                          <Box width={24} />
                        )}
                        {secondaryOrderInfo(order.priority, t("priority"))}
                      </Stack>
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          setOrderDetailsId(order.orderId);
                        }}
                        aria-label={t("view order")}
                      >
                        <ArrowRight24Px />
                      </IconButton>
                    </Stack>
                  </Paper>
                </ListItem>
              );
            })}
        </Stack>
      </Paper>
      <Drawer
        anchor="right"
        open={!!orderDetailsId}
        onClose={() => {
          setOrderDetailsId(null);
        }}
      >
        <Box
          sx={{
            maxWidth: "100%",
            width: "700px",
            paddingTop: "48px"
          }}
        >
          {orderDetailsId && (
            <OrderSidePanel
              onClose={() => setOrderDetailsId(null)}
              orderId={orderDetailsId}
            />
          )}
        </Box>
      </Drawer>
    </>
  );
}
